import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";

import { useAuth } from "./useAuth";

import { AmplitudeTrackingEvent, trackAmplitudeEvent } from "@/lib/amplitude";
import { makeRequest } from "@/network/api";
import { SkySocietyTierId } from "@/pages/Profile/skySocietyTiers";

// Type definitions
export interface SocialConnection {
  discord?: {
    discordId: string;
    username: string;
    displayName: string | null;
    profileImageUrl: string | null;
  };
  twitter?: {
    twitterId: string;
    username: string;
    displayName: string | null;
    profileImageUrl: string | null;
  };
  skySocietyTier: SkySocietyTierId;
}

// Query key
const SOCIAL_CONNECTIONS_QUERY_KEY = "socialConnections";

// Fetch social connections
export function useSocialConnections(address: string | undefined) {
  return useQuery({
    queryKey: [SOCIAL_CONNECTIONS_QUERY_KEY, address],
    queryFn: async (): Promise<SocialConnection> => {
      const response = await makeRequest(
        `user/social-connections?walletAddress=${address}`,
      );
      const data = await response.json();
      return data;
    },
    enabled: Boolean(address),
    refetchOnWindowFocus: false,
  });
}

// Initiate Discord connection
export function useConnectDiscord() {
  const { address } = useAuth();
  return useMutation({
    mutationFn: async () => {
      const frontendUrl = window.location.origin;
      const response = await makeRequest(
        `socials/discord/connect?returnUrl=${encodeURIComponent(frontendUrl)}&walletAddress=${address}`,
      );
      const data = await response.json();
      return data.authUrl;
    },
    onSuccess: (authUrl) => {
      // Open Discord authorization in a new tab
      window.open(authUrl, "_blank");
    },
  });
}

// Initiate Twitter connection
export function useConnectTwitter() {
  const { address } = useAuth();
  return useMutation({
    mutationFn: async () => {
      const frontendUrl = window.location.origin;
      const response = await makeRequest(
        `socials/twitter/connect?returnUrl=${encodeURIComponent(frontendUrl)}&walletAddress=${address}`,
      );
      const data = await response.json();
      return data.authUrl;
    },
    onSuccess: (authUrl) => {
      // Open Twitter authorization in a new tab
      window.open(authUrl, "_blank");
    },
  });
}

export function useDisconnectDiscord() {
  const queryClient = useQueryClient();
  const { address } = useAuth();

  return useMutation({
    mutationFn: async () => {
      const response = await makeRequest(`socials/discord/disconnect`, {
        method: "POST",
      });
      const data = await response.json();
      return data;
    },
    onSuccess: () => {
      // Refetch social connections to get the updated data
      queryClient.invalidateQueries({
        queryKey: [SOCIAL_CONNECTIONS_QUERY_KEY, address],
      });
    },
  });
}

export function useDisconnectTwitter() {
  const queryClient = useQueryClient();
  const { address } = useAuth();

  return useMutation({
    mutationFn: async () => {
      const response = await makeRequest(`socials/twitter/disconnect`, {
        method: "POST",
      });
      const data = await response.json();
      return data;
    },
    onSuccess: () => {
      // Refetch social connections to get the updated data
      queryClient.invalidateQueries({
        queryKey: [SOCIAL_CONNECTIONS_QUERY_KEY, address],
      });
    },
  });
}

// Check for successful social connection after redirect
export function useCheckSocialConnectionStatus() {
  const queryClient = useQueryClient();
  const { address } = useAuth();

  // This will run on component mount and check URL parameters
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const social = urlParams.get("social");
    const status = urlParams.get("status");

    if (social && status === "success") {
      trackAmplitudeEvent(AmplitudeTrackingEvent.PortalConnectedSocials, {
        social_type: social,
      });

      // Clear the URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);

      // Refetch social connections to get the updated data
      queryClient.invalidateQueries({
        queryKey: [SOCIAL_CONNECTIONS_QUERY_KEY, address],
      });

      // Do we want to show a success toast here?
    }
  }, [address, queryClient]);
}
